import React, { FC, useEffect, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import Button from '@vtex/styleguide/lib/Button'
import some from 'lodash/some'
import {
  isWarningState,
  isCanceledState,
  isInvoicedState,
} from '@/checkout-instore-legacy/order/constants'
import Icon from '@/checkout-instore-legacy/components/Commons/Icons'
import * as EmailUtils from '@/checkout-instore-legacy/utils/EmailUtils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'
import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import {
  OrderPlacedState,
  PaymentStatus,
} from '@/checkout-instore-legacy/order/typings'
import {
  extractOrderPlacedHeaderInfo,
  getSalesAssociateCodeFromOrder,
} from '@/checkout-instore-legacy/order/utils'
import { usePrintPage } from '@/checkout-instore-legacy/hooks/usePrintPage'
import { useDispatch } from 'react-redux'

import { CancelTotalOrderButton } from './OrderPlacedDetail/CancelOrderButton'
import OrderPlacedPaymentDetail from './OrderPlacedDetail/OrderPlacedPaymentDetail'

const isOrderCanceledOrInvoiced = ({ state }: Order): boolean =>
  isCanceledState(state) || isWarningState(state) || isInvoicedState(state)

const getPageDetails = (
  ordersState: OrderPlacedState,
  orderState: PaymentStatus
) => {
  switch (ordersState || orderState) {
    case OrderPlacedState.Success || PaymentStatus.Success:
      return {
        iconType: 'successv2',
        iconColor: 'c-success',
        bgColor: 'bg-success--faded',
        orderState: 'orderPlacedSaleConfirmed',
        textStyle: '',
      }

    case OrderPlacedState.Canceled || PaymentStatus.Canceled:
      return {
        iconType: 'canceledv2',
        iconColor: 'c-danger',
        bgColor: 'bg-danger--faded',
        orderState: 'orderPlacedSaleCanceled',
        textStyle: '',
      }

    case OrderPlacedState.Warning ||
      PaymentStatus.RequestCancel ||
      PaymentStatus.CancellationRequested ||
      PaymentStatus.Cancel:
      return {
        iconType: 'warningv2',
        iconColor: 'c-warning',
        bgColor: 'bg-warning--faded',
        orderState: 'orderPlacedSaleWarning',
        textStyle: 'f3-ns',
      }

    case OrderPlacedState.IncompleteFinished || PaymentStatus.PaymentPending:
      return {
        iconType: 'warningv2',
        iconColor: 'c-warning',
        bgColor: 'bg-warning--faded',
        orderState: 'orderPlacedProcessingIncomplete',
        textStyle: '',
      }

    case OrderPlacedState.IncompleteUnfinished ||
      PaymentStatus.PaymentPending ||
      PaymentStatus.WaitingForOrderAuthorization:
      return {
        iconType: 'warningv2',
        iconColor: 'c-warning',
        bgColor: 'bg-warning--faded',
        orderState: 'orderPlacedSaleUnfinished',
        textStyle: '',
      }

    case OrderPlacedState.ECommercePaymentDenied || PaymentStatus.PaymentDenied:
      return {
        iconType: 'canceledv2',
        iconColor: 'c-danger',
        bgColor: 'bg-danger--faded',
        orderState: 'orderPlacedEcommercePaymentDenied',
        textStyle: '',
      }

    case OrderPlacedState.ECommercePaymentPending ||
      PaymentStatus.PaymentPending:
      return {
        iconType: 'warningv2',
        iconColor: 'c-warning',
        bgColor: 'bg-warning--faded',
        orderState: 'orderPlacedEcommercePaymentPending',
        textStyle: '',
      }

    default:
      return {
        iconType: 'successv2',
        iconColor: 'c-success',
        bgColor: 'bg-success--faded',
        orderState: 'orderPlacedSaleConfirmed',
        textStyle: '',
      }
  }
}

const getSubtitleMessage = (order: Order, autoRefresh: AutoRefresh) => {
  const date = order.timeZoneCreationDate?.split('T')[0].split('-') ?? []
  const time = order.timeZoneCreationDate?.split('T')[1].split('.')[0]

  const dateFormatted = `${date[2]}/${date[1]}/${date[0]}`

  switch (autoRefresh.status) {
    case 'none':
      return (
        <FormattedMessage
          id="orders.table.orderDate"
          values={{ date: dateFormatted, time }}
        />
      )

    case 'active':
      return (
        <FormattedMessage
          id="orderPlacedAutoRefreshInTime"
          values={{ remainingTime: autoRefresh.remainingTime }}
        />
      )

    default:
      return <FormattedMessage id="orderPlacedRefreshing" />
  }
}

interface OrderPlacedHeaderProps {
  orders: Order[]
  onNewOrderAction: () => void
  ordersState: OrderPlacedState
  printImmediately: boolean
  actionsDisabled: boolean
  autoRefresh: AutoRefresh
}

interface AutoRefresh {
  status: 'none' | 'active' | 'updating'
  remainingTime: number
}

interface OrderPlacedHeaderActionsProps {
  pagePrinting: boolean
  onCancelOrdersClick: () => void
  onPrintPageClick: () => void
  orders: Order[]
}
type WithRouterOrderPlacedHeaderActionsProps = OrderPlacedHeaderActionsProps & {
  router: RouterProps<any, any>
}

const OrderPlacedHeaderActions: FC<OrderPlacedHeaderActionsProps> = withRouter(
  withMediaQuery<WithRouterOrderPlacedHeaderActionsProps>(
    ({ pagePrinting, orders, onPrintPageClick, mediaQuery, router }) => {
      const handlePrintPage = () => {
        logClickEvent('print-order-page', 'Sale')
        onPrintPageClick()
      }

      const { orderId } = orders[0]

      const isLastOrdersPage = !!router.location.search // check if it's coming from Last Orders menu option
      const {
        enablePrintButton,
        isCancelOrderAlwaysVisible,
      } = getInstoreConfig()

      const isCancelButtonVisible =
        some(orders, (order) => !isOrderCanceledOrInvoiced(order)) &&
        (isCancelOrderAlwaysVisible || isLastOrdersPage)

      const fullWidth = mediaQuery({
        phone: true,
        tablet: true,
        desktop: false,
      })

      const showPrintButton = enablePrintButton && !fullWidth

      return (
        <div className="pv6 pv7-ns ph5 ph8-l bb b--black-10 no-print-spaces-v flex-l flex-row-l">
          {isCancelButtonVisible && (
            <div className="no-print mb6 mb0-l mr6-l">
              <CancelTotalOrderButton orderId={orderId} />
            </div>
          )}
          {showPrintButton && (
            <div id="print-page" className="print-details no-print">
              <Button
                variation="secondary"
                onClick={handlePrintPage}
                isLoading={pagePrinting}
                block={fullWidth}
              >
                <FormattedMessage
                  id="orderPlacedPrintOrder"
                  values={{ numOrders: orders.length }}
                />
              </Button>
            </div>
          )}
        </div>
      )
    }
  )
) as any

export const OrderPlacedHeader: FC<OrderPlacedHeaderProps> = ({
  orders,
  onNewOrderAction,
  ordersState,
  printImmediately,
  actionsDisabled,
  autoRefresh,
}) => {
  const dispatch = useDispatch()
  const { isLoading: pagePrinting, printPage: printPageHtml } = usePrintPage(
    dispatch
  )

  const handlePrintPage = useCallback(async () => {
    const timeToWait = 0

    await printPageHtml(timeToWait)
  }, [printPageHtml])

  useEffect(() => {
    if (printImmediately) {
      handlePrintPage()
    }
  }, [printImmediately, handlePrintPage])

  const handleNewOrderAction = () => {
    logClickEvent('start-new-sale', 'Sale', {
      orderGroup: orders[0].orderGroup,
    })
    onNewOrderAction()
  }

  const order = orders[0]
  const clientProfile = order.clientProfileData
  const { document } = clientProfile
  const isInStoreAnonymousEmail = EmailUtils.isInStoreAnonymousEmail(
    clientProfile.email
  )

  const firstName =
    clientProfile && clientProfile.firstName !== 'Anonymous'
      ? clientProfile.firstName
      : null

  const lastName =
    clientProfile && clientProfile.firstName !== 'Anonymous'
      ? clientProfile.lastName
      : null

  const { printedOrderExtraText } = getInstoreConfig()
  const headerExtraInfo = extractOrderPlacedHeaderInfo(order)
  const state = {
    orderPlacedInfo: headerExtraInfo || {
      state: null,
      invoiceNumber: null,
      invoiceUrl: null,
      trackingNumber: null,
    },
  }

  const {
    iconType,
    iconColor,
    bgColor,
    orderState,
    textStyle,
  } = getPageDetails(ordersState, state.orderPlacedInfo.state)

  const salesAssociateCode = getSalesAssociateCodeFromOrder(order)

  return (
    <div data-testid="order-placed-header" className="bg-base br2-l">
      <div className={`${bgColor} pv7 pv9-ns tc no-print-spaces`}>
        <div className={`no-print ${iconColor} pv3 pv6-ns`}>
          <Icon type={iconType} color="currentColor" size={64} />
        </div>
        <div
          className={`f4 f2-ns fw7 dark-green lh-copy pt3 pb2 pt0-ns pb3-ns ph5 ${textStyle}`}
        >
          <FormattedMessage id={orderState} />
        </div>
        <div className="f6 f4-ns pb3 pb6-ns dark-green">
          {getSubtitleMessage(order, autoRefresh)}
        </div>
        <div className="dn show-print mh5 mt4">{printedOrderExtraText}</div>
        <span className="dn flex-l justify-center center mv6-l no-print">
          <Button variation="primary" onClick={handleNewOrderAction}>
            <FormattedMessage id="orderPlacedStartNewSale" />
          </Button>
        </span>
      </div>
      <div>
        <OrderPlacedPaymentDetail order={order} />
      </div>

      <div className="pv6 pv7-ns ph5 ph8-l bb b--black-10 no-print-spaces-v">
        <div className="f6 f5-ns tl-l">
          {isInStoreAnonymousEmail ? (
            <p>
              <FormattedMessage id="orderPlacedNoClientEmail" />
            </p>
          ) : (
            <div className="lh-copy">
              <span className="dib f4 mid-gray mb3 no-print-spaces">
                <FormattedMessage id="orderPlacedReceiptsSentTo" />
              </span>
              <div>
                <strong>
                  {firstName} {lastName}
                </strong>
              </div>
              <div>{clientProfile.email}</div>
            </div>
          )}
          <div className="lh-copy pv4 no-print-spaces">
            {document ? (
              <>
                <div>
                  <strong>
                    <FormattedMessage id="document" />
                  </strong>
                </div>
                <div>{document}</div>
              </>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
      {salesAssociateCode ? (
        <div className="pv6 pv7-ns ph5 ph8-l bb b--black-10 no-print">
          <div className="lh-copy f6 f5-ns tl-l">
            <div>
              <strong>
                <FormattedMessage id="orderPlacedObservationVendorCode" />
              </strong>
            </div>
            <div>{salesAssociateCode}</div>
          </div>
        </div>
      ) : null}

      {actionsDisabled ? null : (
        <OrderPlacedHeaderActions
          orders={orders}
          pagePrinting={pagePrinting}
          onCancelOrdersClick={handlePrintPage}
          onPrintPageClick={handlePrintPage}
        />
      )}
    </div>
  )
}
